import type { ContentTableRow } from './content-table-rows.ts';

export type ContentTableSortField = 'createdAt' | 'no';
export type ContentTableSortOrder = 'ascend' | 'descend';

export interface ContentTableSort {
  field: ContentTableSortField;
  order: ContentTableSortOrder;
}

export const DEFAULT_CONTENT_TABLE_SORT: ContentTableSort = { field: 'createdAt', order: 'descend' };
export const CONTENT_TABLE_SORT_STORAGE_PREFIX = 'youkuai.content-table-sort';

type StorageReader = Pick<Storage, 'getItem'>;
type StorageWriter = Pick<Storage, 'setItem'>;

export function contentTableSortStorageKey(accountId: string): string {
  return `${CONTENT_TABLE_SORT_STORAGE_PREFIX}:${accountId}`;
}

function isSort(value: unknown): value is ContentTableSort {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<ContentTableSort>;
  return (candidate.field === 'createdAt' || candidate.field === 'no') &&
    (candidate.order === 'ascend' || candidate.order === 'descend');
}

export function readContentTableSort(storage: StorageReader | null | undefined,
  accountId: string): ContentTableSort {
  if (!accountId) return DEFAULT_CONTENT_TABLE_SORT;
  try {
    const value: unknown = JSON.parse(storage?.getItem(contentTableSortStorageKey(accountId)) ?? 'null');
    return isSort(value) ? value : DEFAULT_CONTENT_TABLE_SORT;
  } catch {
    return DEFAULT_CONTENT_TABLE_SORT;
  }
}

export function writeContentTableSort(storage: StorageWriter | null | undefined,
  accountId: string, sort: ContentTableSort): void {
  if (!accountId) return;
  try {
    storage?.setItem(contentTableSortStorageKey(accountId), JSON.stringify(sort));
  } catch {
    // 浏览器禁止本地存储时，当前页的排序仍然可用。
  }
}

const numberCollator = new Intl.Collator('zh-CN', { numeric: true, sensitivity: 'base' });

export function compareContentTableRows(left: ContentTableRow, right: ContentTableRow,
  field: ContentTableSortField): number {
  const compared = field === 'createdAt'
    ? left.createdAt.localeCompare(right.createdAt)
    : numberCollator.compare(left.no, right.no);
  return compared || left.contentId.localeCompare(right.contentId);
}

export function sortContentTableRows(rows: readonly ContentTableRow[], sort: ContentTableSort): ContentTableRow[] {
  const direction = sort.order === 'descend' ? -1 : 1;
  return [...rows].sort((left, right) => direction * compareContentTableRows(left, right, sort.field));
}

export function formatContentCreatedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('zh-CN', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit',
    day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).format(date).replaceAll('/', '-');
}
